import { AnimatedShinyText } from "@/components/ui/magicui/animated-shiny-text";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import { memo } from "react";

/**
 * Tool-specific display messages for skeleton loader
 * Maps tool categories to user-friendly status messages
 */
export const TOOL_MESSAGES = {
  web_search: "Searching the web",
  image_generation: "Generating image",
  image_edit: "Editing image",
  video_generation: "Generating video",
  youtube: "Analyzing video",
  calendar: "Checking calendar",
  gmail: "Checking email",
  drive: "Searching files",
  memory: "Updating memory",
  code_interpreter: "Running code",
  canvas: "Creating canvas",
  places_search: "Searching places",
  firecrawl: "Browsing website",
  mcp: "Using tool",
  unknown: "Working on it...",
};

/**
 * Unified tool skeleton loader component
 * Displays a consistent loading indicator for all tool executions
 *
 * @param {string} toolCategory - The category of tool being executed (from getToolCategory)
 * @param {string} message - Optional custom message (overrides toolCategory lookup)
 * @param {string} className - Additional CSS classes
 */
const ToolSkeletonLoader = memo(function ToolSkeletonLoader({
  toolCategory,
  message,
  className,
}) {
  const displayMessage = message || TOOL_MESSAGES[toolCategory] || TOOL_MESSAGES.unknown;

  return (
    <div
      className={cn(
        "h-8 px-2 rounded-md bg-transparent border-0 transition-colors inline-flex items-center gap-2",
        className
      )}
    >
      <Loader2
        className="h-4 w-4 animate-spin text-muted-foreground"
        strokeWidth={2}
      />
      <AnimatedShinyText className="text-sm text-muted-foreground">
        {displayMessage}
      </AnimatedShinyText>
    </div>
  );
});

ToolSkeletonLoader.propTypes = {
  toolCategory: PropTypes.string,
  message: PropTypes.string,
  className: PropTypes.string,
};

export default ToolSkeletonLoader;
